import { Component, inject } from '@angular/core';
import { MatButtonToggleChange } from '@angular/material/button-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiUser, UsersApi } from '@tytapp/api';
import { AppConfig, BaseComponent } from '@tytapp/common';
import { isOffline } from '@tytapp/environment-utils';
import { UserService } from '@tytapp/user';

interface PrivacyControl {
    title: string;
    subtitle?: string;
    profileProperty?: string;
    visible?: () => boolean;
}

interface VisibilityOption {
    label: string;
    value: string;
    visible?: () => boolean;
}

@Component({
    selector: 'tyt-privacy-controls',
    templateUrl: './privacy-controls.component.html',
    styleUrls: ['./privacy-controls.component.scss']
})
export class PrivacyControlsComponent extends BaseComponent {
    private userApi = inject(UsersApi);
    private userService = inject(UserService);
    private matSnackBar = inject(MatSnackBar);
    private appConfig = inject(AppConfig);

    showDeleteData = false;
    downloadDataEnabled = false;
    state = 'loading';

    downloadDataStarted = false;
    downloadDataSuccess = false;
    dataDownloadErrorMessage: string;

    user: ApiUser;

    async init() {
        this.state = 'loading';
        this.downloadDataEnabled = await this.shell.hasFeature('apps.web.enable_data_download');
        this.subscribe(this.userService.userChanged, user => {
            this.user = user;
            if (isOffline()) {
                this.state = 'offline';
                return;
            }
            this.state = 'ready';
        });

        this.tytnEnabled = await this.appConfig.featureEnabled("apps.web.enable_tytn");
    }

    tytnEnabled = false;

    get appUrl() {
        return '/';
    }

    goTo(section: string) {
        let url;

        if (section === 'delete-data') {
            this.redirection.go(url);
            return;
        }
    }

    async downloadData() {
        try {
            this.downloadDataStarted = true;
            const response = await this.userApi.startDataDownload().toPromise();
            this.downloadDataSuccess = true;
        } catch (error) {
            if (error.json)
                error = error.json();
            this.dataDownloadErrorMessage = error.message || error.error;
        } finally {
            this.downloadDataStarted = false;
        }
    }


    visibilityOptions: VisibilityOption[] = [
        { label: 'Just Me', value: 'self' },
        { label: 'My Followers', value: 'followers', visible: () => this.tytnEnabled },
        { label: 'Everyone', value: 'public' }
    ];

    profileControls: PrivacyControl[] = [
        {
            title: 'Who can view my posts',
            subtitle: 'Includes your posts, mentions of you, and replies.',
            profileProperty: 'post_visibility',
            visible: () => this.tytnEnabled
        },
        {
            title: 'Who can view my activities',
            subtitle: 'Includes likes, shares, follows, voting on a poll, signing petitions.',
            profileProperty: 'activity_visibility',
            visible: () => this.tytnEnabled
        },
        {
            title: 'Who can view my community profile info',
            subtitle:
                'Includes your declared location, interests, "about me", and other personality questions. '
                + 'This does not include demographic information shared with TYT.',
            profileProperty: 'profile_visibility'
        },
        {
            title: 'Who can view my followers',
            profileProperty: 'followers_visibility',
            visible: () => this.tytnEnabled
        },
        {
            title: `Who can view what I'm following`,
            profileProperty: 'following_visibility'
        },
    ];

    loadingUpdate = false;

    async setProfileVisibilityPropertyValue(property: string, change: MatButtonToggleChange) {

        if (this.currentUsername === 'home') {
            return;
        }

        if (this.privacySettings[property] === change.value) {
            return;
        }

        this.loadingUpdate = true;
        this.privacySettings[property] = change.value;
        const newUser = await this.userApi.updateProfile({
            privacy_settings: {
                [property]: change.value
            }
        }).toPromise();
        this.loadingUpdate = false;
        this.matSnackBar.open('Your Profile settings have been updated.', undefined, {
            duration: 3000
        });
    }

    get privacySettings() {
        return this.user?.profile?.privacy_settings;
    }

    get currentUsername() {
        return this.user?.username;
    }

}
